/**
 * Netx.Session manages a user authentication key and/or an access token.
 * It is the first object initialized, and it establishes authentication for the rest of X7.
 * @class
 */

import Base from './netx-model';
import SAMLServicesCollection from '@netx/core-auth/collections/samlServices';
import _ from 'underscore';
import Netx from '@netx/core/bootstrap';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import coreConfig from '@netx/core-next/config';
import {userSource} from '@netx/core-user/properties/user';
import {UserPref} from '@netx/core-user-next/constants';
import {getPreference} from '@netx/core-user-next/utils/getPreference';

import authConfig from '@netx/core-auth/config';
import {getPublicProperty} from '@netx/core-app-next/utils/getPublicProperty';
import {URL} from '@netx/core-next/url';

class Session extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @type {object}
	 */
	get xhrTrackProgress() {
		return {
			getAdvertisedSamlServices: true,
			getPublicMethods: true,
		};
	}

	/**
	 * @type {object}
	 */
	get xhrLoadingMessages() {
		return {
			getAdvertisedSamlServices: 'Checking SAML services',
			getPublicMethods: 'Fetching public methods',
		};
	}

	/**
	 * @type {object}
	 */
	get xhrLoadedMessages() {
		return {
			getAdvertisedSamlServices: 'Checked SAML services',
			getPublicMethods: 'Fetched public methods',
		};
	}

	/**
	 * @property {object}                          defaults                - Default attributes
	 * @property {array}                           defaults.browserInfo    - TODO: document
	 * @property {object}                          defaults.browserLimits  - Lowest supported browser versions
	 * @property {boolean}                         defaults.borwserOk      - Browser version confirmed
	 * @property {string}                          defaults.loginMsg       - Message to display at login screen
	 * @property {string}                          defaults.loginMsgClass  - CSS class for login screen message ( danger, info, success, warning )
	 * @property {string}                          defaults.password       - Password (authentication)
	 * @property {array}                           defaults.publicMethods  - Any array of publicly accessible methods
	 * @property {string}                          defaults.sessionKey     - Session key
	 * @property {string}                          defaults.token          - Token key
	 * @property {string}                          defaults.username       - Username (authentication)
	 * @property {module:core/lib/netx-collection} defaults.samlServices   - SAML services
	 */
	get defaults() {
		return {
			browserInfo: [],
			browserLimits: {
				chrome: 25,
				crios: 1,
				firefox: 20,
				msie: 10,
				phantomjs: 1,
				safari: 5,
				trident: 6,
			},
			browserOk: false,
			loginMsg: '',
			loginMsgClass: '',
			password: '',
			publicMethods: [],
			sessionKey: '',
			token: '',
			username: '',
			loggedInAsOtherUser: false,
			samlServices: new SAMLServicesCollection(),
		};
	}

	/**
	 * @todo: Document why we're using clientAttrs here & what is meant by it?
	 *        There's no calls to session.self() in the codebase that I can find...
	 * @type {array}
	 */
	get clientAttrs() {
		return [
			'browserLimits',
			'loggedInAsOtherUser',
			'loginMsg',
			'loginMsgClass',
			'publicMethods',
		];
	}

	/**
	 * Methods for this model
	 * @type {object}
	 * @memberof Session
	 * @property {object} methods - Methods for this model
	 * @property {method} methods.getPublicMethods   - Get public methods
	 * @property {method} methods.getSamlServices    - Get SAML services for SSO login
	 * @property {method} methods.testSession        - Test if session is valid
	 * @property {method} methods.testToken          - Test if token is valid
	 * @property {method} methods.testAuthentication - Test if authenticated
	 * @property {method} methods.tickle             - Tickle the session (keep alive)
	 * @property {methos} methods.triggerAnAPIError  - Sometimes, while testing, it's useful to be able to trigger an api error by calling an undefined api method
	 */
	get methods() {
		return {
			getPublicMethods: ['getPublicMethods'],
			getSamlServices: ['getAdvertisedSamlServices'],
			testAuthentication: ['getAuthentication'],
			testSession: ['getSelf'],
			testToken: ['isTokenValid', 'token'],
			_tickle: ['getProductVersion'],
			triggerAnAPIError: ['undefinedAPIMethod'],
		};
	}

	/**
	 * Parsers for this model
	 * @type {object}
	 */
	get parsers() {
		return {
			getPublicMethods(methods) {
				this.set({publicMethods: methods});
			},
			/**
			 * Gets saml services and saves them as a collection, used as login childViews
			 * @param {array} samlServices
			 * @param {object} opts Request options
			 */
			getSamlServices(samlServices, opts) {
				// samlServices.push({
				// 	name: 'Custom',
				// 	type: 'saml',
				// 	description: 'Yo, here is a description',
				// 	label: 'Login with Custom',
				// });
				this.get('samlServices').reset(samlServices);
				// this.set({samlServices: new SAMLServicesCollection(samlServices)});
			},
		};
	}

	/** @override */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/**
		 * Used?
		 * @type {string}
		 * @default
		 */
		this.key = opts.key || null;
		/**
		 * 30 minutes
		 * @type {number}
		 * @default
		 */
		this.length = opts.length || 1800000;

		this.resetLogoutTimer = _.debounce(this.resetLogoutTimer, 1000);

		// Auto session tickling
		const sessionTicklers = (this.sessionTicklers = new Base());
		this.addSubModel(sessionTicklers);

		// Listen Up!
		// Session listens for expiration event
		this.listenTo(dispatcher, 'session:expire', this.expireSession)
			.listenTo(dispatcher, 'session:tickle', this.tickle)
			.listenTo(dispatcher, 'session:tickle:add', (key) =>
				sessionTicklers.set(key, true),
			)
			.listenTo(dispatcher, 'session:tickle:remove', (key) =>
				sessionTicklers.unset(key),
			)
			.listenTo(sessionTicklers, 'change', () => {
				this.shouldBeTickling() ? this.startTickling() : this.stopTickling();
			});

		// Listen for route changes:
		this.listenTo(dispatcher, 'routed', this.getToken);
		this.getToken();

		// Set portal context on initial load and then listen
		this.updatePortalContext();

		// Check against this time to figure out how long user has been inactive
		this.lastTickled = this.inactivityStartTime = Date.now();

		// Listen for visbility change
		document.addEventListener(
			'visibilitychange',
			this.visibilityStateChanged.bind(this),
			false,
		);

		// Establish a connection, and trigger 'netx:configured' upon success.
		//
		// These settings from config.js configure our access method.
		// If DAM_URL is set, configure CORS or proxy
		// (But if DAM_URL == the local URL, ignore it.)
		this.configured = new $.Deferred();

		this.testAjax(URL.api, {
			success: (data, status, jqX) => {
				setTimeout(() => {
					this.configured.resolve(data.result);
				}, 0);
			},
		});

		// Avoid oneself not being defined to those who are listening
		_.defer(() => {
			// Until we can handle this better
			if (!this.browserTestOk()) {
				dispatcher.trigger('app:browser:override:confirmed');
				console.warn('Your browser may not be 100% supported.');
			}
		});
	}

	/**
	 * @method
	 * @return {boolean} Authenticated
	 */
	authenticated() {
		return (
			!coreConfig.REQUIRE_LOGIN || this.get('sessionKey') || this.getToken()
		);
	}

	/**
	 * Get session duration
	 * @param {boolean} asMinutes
	 */
	getSessionDuration(asMinutes = false) {
		const defaultMinutes = this.length / 1000 / 60;
		// DAM-14660 - keep the timeout under a 32 bit integer
		const minutes = Math.min(
			getPreference(UserPref.SESSION_DURATION) || defaultMinutes,
			35791,
		);
		return asMinutes ? minutes : minutes * 60 * 1000;
	}

	/**
	 * Get session tickle interval (based on half the defined session duration minus one second)
	 * @method
	 * @return Tickle interval in `ms`
	 */
	getSessionTickleInterval() {
		// We will attempt to "tickle" 4 times in a session duration this way we do not run
		// into anything funny about timing with the response from the server or our debounce
		// of the `resetLogoutTimer` method.
		// This also ensures that if a user had just typed, a short moment after a tickle just
		// occurred, we do not risk the the session timing out if they immediately stop typing
		// after that point
		// Always keep a minimum of 59 seconds between "tickles" (no one should be setting the
		// session duration to less than 2 minutes)
		return Math.max(this.getSessionDuration() / 4, 1000 * 59);
	}

	//////////////////////////////////////////////////
	//
	// General testing methods
	//
	//////////////////////////////////////////////////

	/**
	 * Test for remote DAM access
	 * @param {string} url API endpoint you are trying to access
	 * @param {object} opt AJAX options object (jquery)
	 */
	testAjax(url, opt) {
		return $.ajax({
			url: url,
			type: 'POST',
			cache: false,
			xhrFields: {
				withCredentials: true,
			},
			dataType: 'json',
			contentType: 'application/json; charset=UTF-8',
			data: JSON.stringify({
				dataContext: 'json',
				jsonrpc: '2.0',
				method: 'getPublicProperties',
				id: _.uniqueId(),
				params: [],
			}),
			parseData: false,
			success: opt.success,
			error: opt.error,
		});
	}

	/**
	 * Test for supported browser
	 * @return {boolean} Broser is supported.
	 */
	browserTestOk() {
		var ua = navigator.userAgent,
			N = navigator.appName,
			tem,
			M =
				ua.match(
					/(opera|chrome|safari|firefox|msie|trident|phantomjs|crios)\/?\s*([\d.]+)/i,
				) || [];
		M = M[2] ? [M[1], M[2]] : [N, navigator.appVersion, '-?'];
		if (M && (tem = ua.match(/version\/([.\d]+)/i)) !== null) {
			M[2] = tem[1];
		}

		this.set('browserInfo', M.join(' ').split(' '));

		var browserInfo = this.get('browserInfo');
		var limits = this.get('browserLimits');

		if (browserInfo.length) {
			if (
				(browserInfo[0].toLowerCase() === 'chrome' &&
					parseInt(browserInfo[1]) >= limits.chrome) ||
				(browserInfo[0].toLowerCase() === 'firefox' &&
					parseInt(browserInfo[1]) >= limits.firefox) ||
				(browserInfo[0].toLowerCase() === 'msie' &&
					parseInt(browserInfo[1]) >= limits.msie) ||
				(browserInfo[0].toLowerCase() === 'trident' &&
					parseInt(browserInfo[1]) >= limits.trident) ||
				(browserInfo[0].toLowerCase() === 'phantomjs' &&
					parseInt(browserInfo[1]) >= limits.phantomjs) ||
				(browserInfo[0].toLowerCase() === 'crios' &&
					parseInt(browserInfo[1]) >= limits.crios)
			) {
				this.set('browserOk', true);
			} else if (
				browserInfo[0].toLowerCase() === 'safari' &&
				browserInfo.length === 3 &&
				parseInt(browserInfo[2]) >= limits.safari
			) {
				this.set('browserOk', true);
			}
		}
		// Check that either the browser is actually ok or that there is a cookie override
		if (this.get('browserOk') || this.getCookie('browserOk')) {
			if (this.getCookie('browserOk')) {
				console.warn(
					'This browser %o is not officially supported',
					this.get('browserInfo'),
				);
			}
			return true;
		}
		return false;
	}

	///////////////////////////////////////////////
	//
	// Browser cookie CRUD methods:
	//
	///////////////////////////////////////////////

	/**
	 * getCookie Gets the value of a clientside cookie
	 * @param  {string} cookie
	 * @return {string | undefined}
	 */
	getCookie(cookie) {
		var i,
			x,
			y,
			Cookies = document.cookie.split(';');
		for (i = 0; i < Cookies.length; i++) {
			x = Cookies[i].substr(0, Cookies[i].indexOf('='));
			y = Cookies[i].substr(Cookies[i].indexOf('=') + 1);
			x = x.replace(/^\s|\s+$/g, '');
			if (x == cookie) {
				return unescape(y);
			}
		}
	}

	/**
	 * setCookie Sets the value of a clientside cookie
	 * @param  {string} cname name of cookie
	 * @param  {string} cvalue value of cookies
	 * @param  {number} exdays expire time (in days)
	 * @param  {string} path cookie path (defaults to /)
	 */
	setCookie(cname, cvalue, exdays = 0, path = '/') {
		var expires = '';
		if (exdays) {
			var d = new Date();
			d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000); // converting days to milliseconds  24 hours * 60 minutes * 60 seconds * 1000 millisecs.
			expires = `expires="${d.toUTCString()}";`;
		}
		document.cookie = `${cname}=${escape(cvalue)}; ${expires} path=${path};`;
		// TODO: return the cookie?  Define a cookie object??
	}

	/**
	 * Delete the clientside cookie
	 * @param  {string} name
	 */
	deleteCookie(name) {
		this.setCookie(name, '', -1);
	}

	///////////////////////////////////////////////
	//
	// NetX session key CRUD methods:
	//
	///////////////////////////////////////////////

	/**
	 * Session.getKey() Checks to see if we are holding a session key
	 * @return {Boolean}
	 */
	getKey() {
		// Did we already store it?
		var sessionKey = this.get('sessionKey');

		if (!sessionKey) {
			// Is there a session key in our route?  If so, it should take precedence.
			// (This implements the 'login as other user' feature for admins.
			// Session keys are alphanumeric strings with no other punctuation.
			var match = window.location.hash.match(/^#session\/([0-9a-zA-Z]+)/);
			if (match) {
				sessionKey = match[1];
				this.set({loggedInAsOtherUser: true}); // note how we found this key
			} else {
				// Is a session key cookie set on our browser?
				sessionKey = this.getCookie('sessionKey');
			}
		}

		if (sessionKey) {
			// Cache the key locally.
			this.setKey(sessionKey);
		}

		return sessionKey;
	}

	/**
	 * Set session key
	 * @param {string} key
	 */
	setKey(key) {
		this.set({sessionKey: key});
	}

	/**
	 * Delete session key
	 */
	deleteKey() {
		this.deleteCookie('sessionKey');
		this.set('sessionKey', undefined);
	}

	///////////////////////////////////////////////
	//
	// Access token CRUD methods:
	//
	///////////////////////////////////////////////

	/**
	 * Checks to see if the route contains a token.
	 * @return {string} the token string
	 */
	getToken() {
		// Q: Should we always check the URL, or only check if if we haven't detected a token already?
		// In other words, should the token, once detected, grant access to other routes that don't contain it?
		// A: That use case hasn't come up yet.  Until it does, we always check the URL.

		// Tokens in URLs match the official UUID spec: https://en.wikipedia.org/wiki/Universally_unique_identifier
		// They are "version 4" UUIDs.   The pattern can get more anal that this, but basically it's 36 characters that are either hexadecimal or dash:
		const match =
			window.location.hash.match(/request\/([0-9a-fA-F-]{36})/) || null;

		match == null ? this.deleteToken() : this.setToken(match[1].toString());

		return this.get('token');
	}

	/**
	 * Set token
	 * @param {string} token
	 */
	setToken(token) {
		this.set({token});
	}

	/**
	 * Delete token
	 */
	deleteToken() {
		this.set({token: undefined});
		this.deleteCookie('tokenKey'); // backwards-compatability
	}

	//////////////////////////////////////////////
	//
	// Session management:
	// testing, timing, tickling.
	//
	//////////////////////////////////////////////

	/**
	 * Session.isValid() Return a promise that will resolve or reject on whether we have a valid session,
	 * either via a session key or an access token
	 * @return {Promise}
	 */
	isValid() {
		var d = new $.Deferred();

		// 1) Do we have an access token set?  If so, test it.
		if (this.getToken()) {
			return this.testToken();
		}

		// 2) Do we have a session key at all?  If not, we are S.O.L.
		if (!this.getKey()) {
			return d.reject().promise(); // no session!
		}

		// 3) We have a session key, but is it valid?
		this.testSession().then(
			function (data) {
				// If this is an internal user account, check if we
				// are logged in as public user and reject
				if (
					coreConfig.REQUIRE_LOGIN &&
					data.userSource === userSource.INTERNAL &&
					data.login === getPublicProperty('viewer.userLogin')
				) {
					this.deleteCookie('sessionKey');
					d.reject(); // public user!
				}
				// If not public user resolve
				d.resolve(); // valid session!
			},
			function (data) {
				// If the sessionKey is not valid, erase it
				this.deleteCookie('sessionKey');
				d.reject(); // invalid session!
			},
		);

		return d.promise();
	}

	shouldBeTickling() {
		return (
			authConfig.TICKLE_SESSION ||
			!!Object.keys(this.sessionTicklers.attributes).length
		);
	}

	// Session Tickling
	// ================
	// If enabled, we tickle the server regularly to make sure it doesn't time us out.

	resetTickler() {
		if (!this.tickler && !this.shouldBeTickling()) return;

		this.stopTickling();
		this.startTickling(true);
	}

	/**
	 * Session.startTickling Starts an interval timer to tickle the server regularly
	 */
	startTickling(noInitialTickle = false) {
		if (this.tickler) return;

		const interval = this.getSessionTickleInterval();

		this.tickler = setInterval(this.tickle.bind(this), interval);
		!noInitialTickle && this.tickle();
	}

	/**
	 * Session.stopTickling Removes the interval timer set by startTickling()
	 */
	stopTickling() {
		this.tickler && clearInterval(this.tickler);
		delete this.tickler;
	}

	/**
	 * Session.beTickling Starts the tickling if it hasn't already been started.
	 */
	beTickling() {
		if (this.tickler) return;
		this.startTickling();
	}

	/**
	 * Tickle while preventing over tickling
	 * @method
	 */
	tickle() {
		if (this.getToken() || !this.getKey()) return;

		const now = Date.now();
		// Do not tickle more than once per minute
		if (now - this.lastTickled >= 1000 * 60) {
			this.lastTickled = now;
			this._tickle();
		}
	}

	// Session Timer
	// ================
	// A session timer counts down how many seconds remain before the server times us out.
	// Here, we watch the clock & warn the user when timeout approaches.
	// Note that session tickling prevents session timeout. Use one or the other, but not both.

	/**
	 * resetLogoutTimer starts an interval timer to trigger an event 60 seconds before the DAM logs us out.
	 */
	// NOTE: the function is debounced because it will be called on every successful RPC call.
	resetLogoutTimer() {
		// DAM-16342 - if on a token, never timeout session,
		// never dispatch 'netx:session:timeoutWarning'
		if (this.getToken()) return;

		this.lastTickled = this.inactivityStartTime = Date.now();
		const timeoutMinutes = this.getSessionDuration(true);

		if (this.timer) {
			clearTimeout(this.timer);
		}

		this.timer = setTimeout(() => {
			// Only trigger the warning if we are actually logged in, via key or token
			// and if the page is hidden, don't trigger the timeout warning
			if (this.getKey() && document.visibilityState === 'visible') {
				dispatcher.trigger('netx:session:timeoutWarning');
			}
		}, 60 * 1000 * (timeoutMinutes - 1)); // time out 1 minute (60000 milliseconds) before the session duration (which is expressed in minutes)

		// Accidentally removed in 8.8, leading to the "why am i logged out when i reload" problem:
		this.postponeSessionTimeout(timeoutMinutes);
		// (In 8.9 we will manage the session entirely on the client, so this will change ...)
	}

	/**
	 * Manipulate cookies to signal that the session was ended due to a timeout.
	 * (So that the login screen display a relelevant message.)
	 */
	expireSession() {
		// DAM-16342 - if on a token, never timeout session
		// just in case session:expire is dispatched
		if (this.getToken() || !this.getKey()) {
			return;
		}

		this.deleteCookie('sessionKey');
		this.setCookie('sessionExpired', 'EXPIRED', 1);
		dispatcher.trigger('app:stop');
	}

	/**
	 * Postpone session cookie expiration: set the expire time of the session cookie (if any) to 30 minutes from now.
	 * @param {number} duration Minutes
	 */
	postponeSessionTimeout(duration) {
		var cookie = this.getCookie('sessionKey');
		if (!cookie) return false;

		if (!duration) duration = this.getSessionDuration(true);

		// NOTE: converting duration from minutes to days: 60 mins * 24 hours = 1 day.
		this.setCookie('sessionKey', cookie, duration / (60 * 24));

		return true;
	}

	/**
	 * Check for, and clear, the session-expired cookie.
	 */
	sessionExpired() {
		if (this.getCookie('sessionExpired') == 'EXPIRED') {
			this.deleteCookie('sessionExpired');
			return true;
		}
		return false;
	}

	/**
	 * Update the portalContext cookie to reflect the current theme
	 */
	updatePortalContext() {
		// Update the portalContext cookie to reflect the current theme
		this.setCookie('portalContext', window.location.pathname);
	}

	/**
	 * The browser page visibility has changed
	 */
	visibilityStateChanged() {
		if (document.visibilityState === 'visible') {
			// Set 'portalContext' cookie (DAM-9257)
			this.updatePortalContext();

			// If user backgrounds Netx tab for longer than session,
			// they should be kicked to login screen when tab becomes active.
			const sessionDuration = this.getSessionDuration();

			const sessionExpired =
				Date.now() - this.inactivityStartTime >= sessionDuration;

			if (sessionExpired) {
				// If multiple Netx tabs are open, this timeout could be triggered.
				// Let's ask the server if the session really is expired
				this.testSession()
					.then(this.resetLogoutTimer.bind(this))
					.catch(this.expireSession.bind(this));
			}
		}
	}

	/** @type {string} */
	static get __name__() {
		return 'Session';
	}
}

export default Session;
